// Brand-aware size guide for the Archivo theme (Horizon-based).
//
// Reads a JSON config (script[data-size-guide-config], rendered by
// snippets/size-guide.liquid), adds a subtle "Guía de talles" link next to the
// Talle variant option, and opens a modal showing only the detected brand's
// charts via sub-type pills (e.g. Yagmour: Parte superior / Pantalones / Jeans).
//
// Each chart is a native HTML <table> rendered by Liquid inside a
// <template data-size-guide-chart="id">; this script injects the matching
// template when a pill is selected. The default sub-type is computed in Liquid
// from the product title/type/tags.

(function () {
  'use strict';

  function getConfig() {
    const el = document.querySelector('script[data-size-guide-config]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  const config = getConfig();
  if (!config || !Array.isArray(config.groups) || !config.groups.length) return;

  // Flat lookup: id -> { item, groupIndex }
  const byId = {};
  config.groups.forEach((g, gi) => {
    (g.items || []).forEach((it) => {
      byId[it.id] = { item: it, groupIndex: gi };
    });
  });

  const ids = Object.keys(byId);
  if (!ids.length) return;

  let currentId = byId[config.default] ? config.default : ids[0];

  // ====== Talle variant option detection (controls button placement) ======
  function isTalleElement(el) {
    if (!el) return false;
    // Multi-value option: <fieldset> with <legend>Talle</legend>
    const legend = el.querySelector(':scope > legend');
    if (legend) {
      const text = (legend.textContent || '').trim().toLowerCase();
      return text.startsWith('talle') || text.startsWith('size') || text.startsWith('talla');
    }
    // Single-value option: <div class="variant-option"> with text "Talle ..."
    const text = (el.textContent || '').trim().toLowerCase();
    return text.startsWith('talle') || text.startsWith('size') || text.startsWith('talla');
  }

  function findTalleFieldset() {
    const candidates = document.querySelectorAll(
      'fieldset.variant-option, fieldset[data-option-name], .variant-option[data-testid="variant-option-single"]'
    );
    for (const el of candidates) {
      if (isTalleElement(el)) return el;
    }
    return null;
  }

  // ====== Modal ======
  let modalEl = null;
  let tabsEl = null;
  let itemsEl = null;
  let chartEl = null;
  let lastFocused = null;

  // Look up the HTML table rendered by Liquid for a given chart id.
  function chartHtmlFor(id) {
    const tpl = document.querySelector(
      'template[data-size-guide-chart="' + (window.CSS && CSS.escape ? CSS.escape(id) : id) + '"]'
    );
    return tpl ? tpl.innerHTML : '';
  }

  function isOpen() {
    return modalEl && modalEl.getAttribute('aria-hidden') === 'false';
  }

  // Render the sub-type pills for a given group, marking `activeId` active.
  function renderItems(groupIndex, activeId) {
    const group = config.groups[groupIndex];
    const items = (group && group.items) || [];
    itemsEl.innerHTML = '';

    // A single sub-type doesn't need its own row of pills.
    if (items.length <= 1) {
      itemsEl.hidden = true;
      return;
    }
    itemsEl.hidden = false;

    items.forEach((it) => {
      const pill = document.createElement('button');
      pill.type = 'button';
      pill.className = 'size-guide-modal__pill';
      pill.textContent = it.label;
      if (it.id === activeId) pill.classList.add('size-guide-modal__pill--active');
      pill.addEventListener('click', () => setCurrent(it.id));
      itemsEl.appendChild(pill);
    });
  }

  function setCurrent(id) {
    if (!byId[id]) id = byId[config.default] ? config.default : ids[0];
    currentId = id;
    const entry = byId[id];

    // Group tabs active state
    if (tabsEl) {
      Array.from(tabsEl.children).forEach((tab) => {
        const active = Number(tab.dataset.groupIndex) === entry.groupIndex;
        tab.classList.toggle('size-guide-modal__tab--active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
      });
    }

    // Sub-type pills
    renderItems(entry.groupIndex, id);

    // Chart table
    if (chartEl) {
      chartEl.innerHTML = chartHtmlFor(id);
    }
  }

  function selectGroup(groupIndex) {
    const group = config.groups[groupIndex];
    const items = (group && group.items) || [];
    if (!items.length) return;
    setCurrent(items[0].id);
  }

  function buildTabs() {
    const tabs = document.createElement('div');
    tabs.className = 'size-guide-modal__tabs';
    tabs.setAttribute('role', 'tablist');
    config.groups.forEach((g, gi) => {
      if (!g.items || !g.items.length) return;
      const tab = document.createElement('button');
      tab.type = 'button';
      tab.className = 'size-guide-modal__tab';
      tab.setAttribute('role', 'tab');
      tab.dataset.groupIndex = String(gi);
      tab.textContent = g.label || '';
      tab.addEventListener('click', () => selectGroup(gi));
      tabs.appendChild(tab);
    });
    return tabs;
  }

  function ensureModal() {
    if (modalEl) return modalEl;

    modalEl = document.createElement('div');
    modalEl.className = 'size-guide-modal';
    modalEl.setAttribute('aria-hidden', 'true');
    modalEl.setAttribute('role', 'dialog');
    modalEl.setAttribute('aria-modal', 'true');
    modalEl.setAttribute('aria-label', config.title || 'Guía de talles');

    const backdrop = document.createElement('div');
    backdrop.className = 'size-guide-modal__backdrop';
    backdrop.setAttribute('data-close', '');

    const dialog = document.createElement('div');
    dialog.className = 'size-guide-modal__dialog';
    dialog.setAttribute('role', 'document');

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'size-guide-modal__close';
    closeBtn.setAttribute('data-close', '');
    closeBtn.setAttribute('aria-label', 'Cerrar guía de talles');
    closeBtn.innerHTML =
      '<svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true">' +
      '<line x1="3" y1="3" x2="13" y2="13"></line><line x1="13" y1="3" x2="3" y2="13"></line></svg>';

    const title = document.createElement('h2');
    title.className = 'size-guide-modal__title';
    title.textContent = config.title || 'Guía de talles';

    tabsEl = buildTabs();
    // With a single brand group the top-level tab is redundant — hide it and
    // let the sub-type pills (Bebés / Toddlers / Kids) drive the selection.
    if (config.groups.length <= 1) tabsEl.hidden = true;

    itemsEl = document.createElement('div');
    itemsEl.className = 'size-guide-modal__items';

    chartEl = document.createElement('div');
    chartEl.className = 'size-guide-modal__chart size-guide-block';

    dialog.appendChild(closeBtn);
    dialog.appendChild(title);
    dialog.appendChild(tabsEl);
    dialog.appendChild(itemsEl);
    dialog.appendChild(chartEl);
    modalEl.appendChild(backdrop);
    modalEl.appendChild(dialog);
    document.body.appendChild(modalEl);

    modalEl.addEventListener('click', (e) => {
      if (e.target.closest('[data-close]')) closeModal();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && isOpen()) closeModal();
    });

    return modalEl;
  }

  function openModal() {
    ensureModal();
    lastFocused = document.activeElement;
    setCurrent(currentId);
    modalEl.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    const closeBtn = modalEl.querySelector('.size-guide-modal__close');
    if (closeBtn) closeBtn.focus();
  }

  function closeModal() {
    if (!modalEl) return;
    modalEl.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    if (lastFocused && typeof lastFocused.focus === 'function') {
      try {
        lastFocused.focus();
      } catch (e) {}
    }
  }

  // ====== Trigger button ======
  let triggerEl = null;

  function ensureTrigger() {
    if (triggerEl && triggerEl.isConnected) return;

    const fieldset = findTalleFieldset();
    if (!fieldset) return;

    triggerEl = document.createElement('button');
    triggerEl.type = 'button';
    triggerEl.className = 'size-guide-trigger';
    triggerEl.setAttribute('data-size-guide-trigger', '');
    triggerEl.textContent = config.buttonLabel || 'Guía de talles';

    fieldset.insertAdjacentElement('afterend', triggerEl);
  }

  // ====== Init ======
  function init() {
    ensureTrigger();

    // Delegated click: opens on the first click even if the variant picker
    // re-rendered and swapped the button for a fresh node (the old per-button
    // listener would be lost on that node, needing a second click).
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-size-guide-trigger]');
      if (trigger) {
        e.preventDefault();
        openModal();
      }
    });

    // Re-attach the trigger if the variant picker re-renders and drops it.
    const observer = new MutationObserver(() => {
      if (!triggerEl || !triggerEl.isConnected) ensureTrigger();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
